/**
 * Clive — click-to-play facade for YouTube product media (perf, 2026-09-12).
 *
 * snippets/media.liquid emits `external_video_tag` iframes with their `src` rewritten to
 * `data-clive-video-src`, so the browser never fetches YouTube's player on page load. A
 * YouTube embed costs ~1,047KB the moment it enters the document (base.js 472KB +
 * ytembeds.base 339KB + www-player.css 57KB and friends, measured live on the M58 PDP), and
 * the LUCCA M58 has ONE video that media.liquid renders TWICE — once for the mobile carousel
 * modal, once for the desktop gallery. That megabyte was the single largest asset on the page
 * and the main reason the LCP hero spent 6.1s in "Load Delay".
 *
 * This file puts the src back at the moment a visitor actually asks for the video. Both
 * surfaces were already click-to-play, so nothing changes for the user except that the page
 * loads a second faster.
 *
 * Two surfaces, two triggers:
 *
 *   Desktop gallery — media.liquid renders a `.clive-video-facade` button over the iframe.
 *     Click it: activate the sibling iframe, remove the poster.
 *
 *   Mobile carousel — Stiletto reads the iframe's `outerHTML` into PhotoSwipe as the lightbox
 *     `html` dataSource and opens it on `.mobile-media-carousel__poster` click
 *     (assets/theme.js:4944-4958). The iframe that ends up on screen is therefore a CLONE
 *     injected later, not the one in our markup — so there is nothing to bind a click to.
 *     A MutationObserver catches the clone as PhotoSwipe inserts it and activates it there.
 *
 * Deliberately dependency-free and deferred: if this file fails to load, the only consequence
 * is that the videos do not play. Nothing else on the PDP relies on it.
 */
(function () {
  'use strict';

  var ATTR = 'data-clive-video-src';

  /**
   * Move data-clive-video-src back to src. Idempotent — the MutationObserver and a click can
   * both reach the same node, and PhotoSwipe may re-insert its content.
   */
  function activate(iframe) {
    if (!iframe) return false;
    var src = iframe.getAttribute(ATTR);
    if (!src) return false;
    iframe.removeAttribute(ATTR);
    iframe.setAttribute('src', src);
    return true;
  }

  /* ---------------------------------------------------------------------- */
  /* Desktop gallery: the poster button sits next to the iframe.            */
  /* ---------------------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    var poster = event.target.closest('[data-clive-video-facade]');
    if (!poster) return;

    event.preventDefault();

    var container = poster.parentNode;
    if (!container) return;

    // autoplay=1 so the click that dismissed the poster also starts playback — without it the
    // visitor would have to press play a second time inside the YouTube chrome.
    var iframe = container.querySelector('iframe[' + ATTR + ']');
    if (iframe) {
      var src = iframe.getAttribute(ATTR);
      if (src && src.indexOf('autoplay=') === -1) {
        iframe.setAttribute(ATTR, src + (src.indexOf('?') === -1 ? '?' : '&') + 'autoplay=1');
      }
      activate(iframe);
    }

    poster.remove();
  });

  /* ---------------------------------------------------------------------- */
  /* Mobile carousel: catch the clone PhotoSwipe injects into its lightbox. */
  /* ---------------------------------------------------------------------- */

  function activateWithin(root) {
    if (!root || root.nodeType !== 1) return;
    if (root.matches && root.matches('iframe[' + ATTR + ']')) {
      activate(root);
      return;
    }
    if (!root.querySelectorAll) return;
    var found = root.querySelectorAll('iframe[' + ATTR + ']');
    for (var i = 0; i < found.length; i++) activate(found[i]);
  }

  function startObserver() {
    if (typeof MutationObserver !== 'function') return;

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;

          // Only act inside PhotoSwipe. Scoping this matters: the desktop gallery iframe is
          // also in the document with the same attribute, and it must stay deferred until its
          // own poster is clicked. An unscoped observer would activate both and give the
          // megabyte straight back.
          if (node.closest && node.closest('.pswp')) {
            activateWithin(node);
          } else if (node.classList && node.classList.contains('pswp')) {
            activateWithin(node);
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserver);
  } else {
    startObserver();
  }
})();
